import type {
  GetStaticPathsResult,
  GetStaticPropsResult,
  NextPage,
} from 'next';
import Script from 'next/script';
import { NextSeo } from 'next-seo';
import * as React from 'react';

import Layout from '@/components/layout/Layout';

import { SITE_URL } from '../../lib/constants';
import { getAllPostIds, getPostData, PostData } from '../../lib/posts';

type PostProps = {
  // Metadata
  id: string;
  title: string;
  subtitle: string | null;
  author: string;
  date: string;
  image: string | null;
  subscribe?: boolean;
  coverImage: string;
  // Content
  content: string;
};

export async function getStaticProps({
  params,
}: PostData): Promise<GetStaticPropsResult<PostProps>> {
  let postData;
  try {
    postData = await getPostData(params.id);
  } catch (e) {
    return { notFound: true };
  }

  //
  if (!postData || !postData.title || !postData.date || !postData.contentHtml) {
    return { notFound: true };
  }

  // Log information about posts for SEO debugging.
  console.log(`getStaticProps: blog/${params.id}`);
  // console.table([
  //   {name: 'title', length: postData.title.length, valid: postData.title.length <= 55},
  //   {name: 'description', length: postData.description.length, valid: postData.description.length <= 160}
  // ]);

  // Note: all CMS fields that are optional must
  // have `|| null` applied below because `undefined` is
  // not JSON-serializable.
  return {
    props: {
      // Metadata
      id: params.id,
      title: postData.title,
      subtitle: postData.subtitle || null,
      author: postData.author,
      date: postData.date,
      image: postData.image || null,
      subscribe: postData.subscribe || true,
      coverImage: postData.coverImage || null,
      // Content
      content: postData.contentHtml,
    },
  };
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const paths = getAllPostIds();
  return {
    paths,
    fallback: false,
  };
}

const Post: NextPage<PostProps> = (props) => {
  const {
    id,
    title,
    subtitle,
    author,
    date,
    content,
    image,
    coverImage,
    subscribe,
  } = props;

  return (
    <>
      <Script
        src='https://platform.twitter.com/widgets.js'
        strategy='beforeInteractive'
      />
      <NextSeo
        title={title}
        description={subtitle || undefined}
        openGraph={{
          title: title,
          url: `${SITE_URL}/blog/${id}`,
          type: 'article',
          images: [
            {
              url: image != null ? SITE_URL + image : SITE_URL + coverImage,
              width: 1200,
              height: 628,
              type: 'image/jpeg',
            },
          ],
        }}
      />
      <Layout mode='light'>
        {/**
         * React doesn't <script> tags embedded via dangerouslySetInnerHtml if the
         * tags are mounted after page load. This prevents widgets like Twitter from showing
         * correctly without a full page refresh. Instead, we embed necessary widget scripts
         * on all blog/[id] pages to make sure things display correctly.
         * More here: https://nextjs.org/docs/basic-features/script
         */}
        <div className='mx-auto w-full max-w-[720px] space-y-12'>
          <div className='border-gray-[#E5E7ED] space-y-6 border-b pt-12'>
            {/*<BackButton path="/blog" mode="light" />*/}
            <p className='text-md text-slate-500'>
              {author} &middot; {date}
            </p>
            <div className='space-y-2'>
              <h1 className='text-3xl font-bold text-slate-900 md:text-5xl'>
                {title}
              </h1>
              <div className='pt-3'>
                <img
                  // className='aspect-[9/6] w-full rounded object-cover pt-3'
                  className='block rounded'
                  src={coverImage}
                  alt={title}
                />
              </div>
              {subtitle ? (
                <h2 className='pt-3 pb-4 text-2xl font-light text-slate-500'>
                  {subtitle}
                </h2>
              ) : null}
            </div>
          </div>
          <div
            className='prose font-sans text-[19px] font-light leading-10'
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      </Layout>
    </>
  );
};

export default Post;
